import { useEffect, useState } from 'react'
import { Send, CheckCircle, XCircle, Plus, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react'
import Topbar from '../components/Topbar'
import api from '../api/client'
import useAppStore from '../store/appStore'
import { fmtDate, fmtEuro, hoje, periodoAtual } from '../utils/fmt'

export default function Quotas() {
  const { condominioAtivo } = useAppStore()
  const [fracoes, setFracoes] = useState([])
  const [historico, setHistorico] = useState([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('fracoes')
  const [modalLancar, setModalLancar] = useState(false)
  const [modalExtra, setModalExtra] = useState(null)
  const [lancarForm, setLancarForm] = useState({ periodo: periodoAtual(), data: hoje() })
  const [extraForm, setExtraForm] = useState({ descricao: '', valor: '', data: hoje(), periodo: periodoAtual() })
  const [resultado, setResultado] = useState(null)
  const [saving, setSaving] = useState(false)
  const [erro, setErro] = useState('')
  const [mesVisualizar, setMesVisualizar] = useState(periodoAtual())

  const load = () => {
    if (!condominioAtivo) return
    setLoading(true)
    Promise.all([
      api.get(`/quotas/${condominioAtivo.id}/fracoes`),
      api.get(`/quotas/${condominioAtivo.id}/historico`)
    ]).then(([f, h]) => {
      setFracoes(f.data)
      setHistorico(h.data)
    }).finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [condominioAtivo])

  const periodoLancado = (fracao, periodo) =>
    fracao.movimentosCC?.some(m => m.periodo === periodo && m.categoria === 'QUOTA') || false

  const mudarMes = (delta) => {
    const [ano, mes] = mesVisualizar.split('-').map(Number)
    let novoMes = mes + delta
    let novoAno = ano
    if (novoMes > 12) { novoMes = 1; novoAno++ }
    if (novoMes < 1) { novoMes = 12; novoAno-- }
    setMesVisualizar(`${novoAno}-${String(novoMes).padStart(2, '0')}`)
  }

  const handleAnularQuota = async (fracao, periodo) => {
    if (!window.confirm(`Anular quota de ${fmtMes(periodo)} para a fração ${fracao.designacao}?\nIsto remove o movimento da conta corrente e a receita correspondente.`)) return
    try {
      await api.delete(`/quotas/${condominioAtivo.id}/anular`, { data: { fracaoId: fracao.id, periodo } })
      load()
    } catch (err) { alert(err.response?.data?.erro || 'Erro ao anular quota.') }
  }

  const handleLancarTodas = async (e) => {
    e.preventDefault(); setSaving(true); setErro(''); setResultado(null)
    try {
      const { data } = await api.post(`/quotas/${condominioAtivo.id}/lancar`, lancarForm)
      setResultado(data); load()
    } catch (err) { setErro(err.response?.data?.erro || 'Erro ao lançar quotas.') }
    finally { setSaving(false) }
  }

  const handleLancarExtra = async (e) => {
    e.preventDefault(); setSaving(true); setErro('')
    try {
      await api.post(`/cc/${modalExtra.id}/movimento`, {
        tipo: 'CREDITO', categoria: 'OUTRO',
        descricao: extraForm.descricao, valor: parseFloat(extraForm.valor),
        data: extraForm.data, periodo: extraForm.periodo || null
      })
      await api.post('/receitas', {
        condominioId: condominioAtivo.id, fracaoId: modalExtra.id,
        data: extraForm.data, valor: parseFloat(extraForm.valor),
        descricao: extraForm.descricao, categoria: 'OUTRO', periodo: extraForm.periodo || null
      })
      setModalExtra(null)
      setExtraForm({ descricao: '', valor: '', data: hoje(), periodo: periodoAtual() })
      load()
    } catch (err) { setErro(err.response?.data?.erro || 'Erro.') }
    finally { setSaving(false) }
  }

  const mesAtual = periodoAtual()
  const eMesAtual = mesVisualizar === mesAtual
  const totalQuotas = fracoes.reduce((s, f) => s + (f.valorQuota || 0), 0)
  const semQuota = fracoes.filter(f => !f.valorQuota || f.valorQuota <= 0).length
  const fracoesCom = fracoes.filter(f => f.valorQuota > 0).length
  const lancadasNoMes = fracoes.filter(f => periodoLancado(f, mesVisualizar)).length

  const fmtMes = (p) => {
    const [a, m] = p.split('-')
    return new Date(parseInt(a), parseInt(m) - 1, 1)
      .toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' })
  }

  return (
    <>
      <Topbar title="Quotas" actions={
        <button className="btn btn-sm btn-primary" onClick={() => { setErro(''); setResultado(null); setLancarForm({ periodo: mesVisualizar, data: hoje() }); setModalLancar(true) }}>
          <Send size={13} /> Lançar quotas do mês
        </button>
      } />
      <div className="page-content">
        <div className="page-header">
          <h1>Gestão de quotas</h1>
          <p>
            Total mensal: <strong>{fmtEuro(totalQuotas)}</strong>
            {' · '}{fmtMes(mesVisualizar)}: <strong style={{ color: lancadasNoMes === fracoesCom ? 'var(--green-dark)' : 'var(--amber-dark)' }}>
              {lancadasNoMes}/{fracoesCom} lançadas
            </strong>
            {semQuota > 0 && <span style={{ color: 'var(--amber-dark)', marginLeft: 8 }}>· ⚠ {semQuota} sem valor definido</span>}
          </p>
        </div>

        {semQuota > 0 && (
          <div className="alert alert-info" style={{ marginBottom: 16 }}>
            Para definir o valor da quota vai a <strong>Configuração → Frações</strong>.
          </div>
        )}

        <div className="tabs">
          <button className={`tab ${tab === 'fracoes' ? 'active' : ''}`} onClick={() => setTab('fracoes')}>Quotas por fração</button>
          <button className={`tab ${tab === 'historico' ? 'active' : ''}`} onClick={() => setTab('historico')}>Histórico</button>
        </div>

        {loading ? <div className="loading-center"><span className="spinner" /></div> : (
          <>
            {tab === 'fracoes' && (
              <div className="card">
                {/* Barra de navegação de meses — mais destacada */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
                  padding: '12px 16px',
                  background: 'var(--green)', borderRadius: 'var(--radius)',
                  boxShadow: '0 2px 8px rgba(29,158,117,0.25)'
                }}>
                  <button onClick={() => mudarMes(-1)} style={{
                    background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6,
                    width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: '#fff'
                  }}>
                    <ChevronLeft size={18} />
                  </button>

                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontWeight: 700, fontSize: 16, color: '#fff', textTransform: 'capitalize' }}>
                      {fmtMes(mesVisualizar)}
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', marginTop: 1 }}>
                      {lancadasNoMes}/{fracoesCom} frações com quota lançada
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {!eMesAtual && (
                      <button onClick={() => setMesVisualizar(mesAtual)} style={{
                        background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)',
                        borderRadius: 6, padding: '4px 10px', fontSize: 11, color: '#fff', cursor: 'pointer'
                      }}>
                        Mês atual
                      </button>
                    )}
                    <button onClick={() => mudarMes(1)} disabled={eMesAtual} style={{
                      background: eMesAtual ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
                      border: 'none', borderRadius: 6,
                      width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: eMesAtual ? 'default' : 'pointer', color: eMesAtual ? 'rgba(255,255,255,0.3)' : '#fff'
                    }}>
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>

                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Fração</th>
                        <th>Condómino</th>
                        <th>Quota/mês</th>
                        <th style={{ textAlign: 'center' }}>Estado</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fracoes.length === 0
                        ? <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 32 }}>Sem frações.</td></tr>
                        : fracoes.map(f => {
                          const lancado = periodoLancado(f, mesVisualizar)
                          return (
                            <tr key={f.id}>
                              <td><strong>{f.designacao}</strong></td>
                              <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                                {f.condominosAssociados?.filter(cf => cf.ativo).map(cf => cf.condomino.nome).join(', ') || '—'}
                              </td>
                              <td>
                                {f.valorQuota > 0
                                  ? <span style={{ fontWeight: 600, color: 'var(--green-dark)' }}>{fmtEuro(f.valorQuota)}</span>
                                  : <span className="badge badge-amber">Não definido</span>}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {f.valorQuota > 0
                                  ? lancado
                                    ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--green-dark)', fontSize: 12, fontWeight: 500 }}>
                                        <CheckCircle size={14} /> Lançada
                                      </span>
                                    : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--amber-dark)', fontSize: 12, fontWeight: 500 }}>
                                        <XCircle size={14} /> Por lançar
                                      </span>
                                  : <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>—</span>
                                }
                              </td>
                              <td>
                                <div style={{ display: 'flex', gap: 6 }}>
                                  {/* Lançar — só aparece se não estiver lançada */}
                                  {f.valorQuota > 0 && !lancado && (
                                    <LancarFracao fracao={f} condominioId={condominioAtivo.id} periodo={mesVisualizar} onDone={load} />
                                  )}
                                  {/* Anular — só aparece se estiver lançada */}
                                  {lancado && (
                                    <button className="btn btn-sm" style={{ color: 'var(--red-dark)' }}
                                      onClick={() => handleAnularQuota(f, mesVisualizar)}
                                      title="Anular quota deste mês">
                                      <Trash2 size={12} /> Anular
                                    </button>
                                  )}
                                  {/* Extra — sempre disponível */}
                                  <button className="btn btn-sm" onClick={() => {
                                    setExtraForm({ descricao: '', valor: '', data: hoje(), periodo: mesVisualizar })
                                    setErro(''); setModalExtra(f)
                                  }} title="Quota extra">
                                    <Plus size={12} /> Extra
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === 'historico' && (
              <div className="card">
                <div className="table-wrap">
                  <table className="data-table">
                    <thead><tr><th>Fração</th><th>Valor</th><th>Data</th><th>Motivo</th></tr></thead>
                    <tbody>
                      {historico.length === 0
                        ? <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 32 }}>Sem histórico.</td></tr>
                        : historico.map(h => (
                          <tr key={h.id}>
                            <td>{h.fracao ? <strong>{h.fracao.designacao}</strong> : <span style={{ color: 'var(--text-secondary)' }}>Geral</span>}</td>
                            <td style={{ fontWeight: 600 }}>{fmtEuro(h.valorMensal)}</td>
                            <td>{fmtDate(h.dataInicio)}</td>
                            <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{h.motivo || '—'}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal lançar todas */}
      {modalLancar && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModalLancar(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Lançar quotas — todas as frações</span>
              <button onClick={() => setModalLancar(false)} style={{ background: 'none', border: 'none', fontSize: 20 }}>×</button>
            </div>
            {!resultado ? (
              <>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                  Regista o pagamento recebido em todas as frações com quota definida.
                </p>
                {semQuota > 0 && <div className="alert alert-info" style={{ marginBottom: 12 }}>{semQuota} fração(ões) sem valor serão ignoradas.</div>}
                {erro && <div className="alert alert-error">{erro}</div>}
                <form onSubmit={handleLancarTodas}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div className="form-group">
                      <label className="form-label">Período *</label>
                      <input className="form-input" type="month" value={lancarForm.periodo} onChange={e => setLancarForm({ ...lancarForm, periodo: e.target.value })} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Data de lançamento *</label>
                      <input className="form-input" type="date" value={lancarForm.data} onChange={e => setLancarForm({ ...lancarForm, data: e.target.value })} required />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn" onClick={() => setModalLancar(false)}>Cancelar</button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? <span className="spinner" /> : 'Lançar'}</button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <div className="alert alert-success">{resultado.mensagem}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                  <div style={{ padding: 12, background: 'var(--bg)', borderRadius: 'var(--radius)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Frações lançadas</div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{resultado.totalFracoes}</div>
                  </div>
                  <div style={{ padding: 12, background: 'var(--bg)', borderRadius: 'var(--radius)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Total recebido</div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{fmtEuro(resultado.totalValor)}</div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-primary" onClick={() => setModalLancar(false)}>Fechar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal quota extra */}
      {modalExtra && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModalExtra(null)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Quota extra — Fração {modalExtra.designacao}</span>
              <button onClick={() => setModalExtra(null)} style={{ background: 'none', border: 'none', fontSize: 20 }}>×</button>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Pagamento adicional pontual — obras, fundo de reserva extra, penalização ou outro encargo.
            </p>
            {erro && <div className="alert alert-error">{erro}</div>}
            <form onSubmit={handleLancarExtra}>
              <div className="form-group">
                <label className="form-label">Descrição *</label>
                <input className="form-input" value={extraForm.descricao}
                  onChange={e => setExtraForm({ ...extraForm, descricao: e.target.value })}
                  placeholder="ex: Obra de pintura, Fundo de reserva extraordinário..." required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Valor (€) *</label>
                  <input className="form-input" type="number" step="0.01" min="0" value={extraForm.valor}
                    onChange={e => setExtraForm({ ...extraForm, valor: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Data *</label>
                  <input className="form-input" type="date" value={extraForm.data}
                    onChange={e => setExtraForm({ ...extraForm, data: e.target.value })} required />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Período (opcional)</label>
                <input className="form-input" type="month" value={extraForm.periodo}
                  onChange={e => setExtraForm({ ...extraForm, periodo: e.target.value })} />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => setModalExtra(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <span className="spinner" /> : 'Lançar quota extra'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}

function LancarFracao({ fracao, condominioId, periodo, onDone }) {
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState({ periodo, data: hoje() })
  const [saving, setSaving] = useState(false)
  const [erro, setErro] = useState('')
  const [sucesso, setSucesso] = useState('')

  useEffect(() => { setForm(f => ({ ...f, periodo })) }, [periodo])

  const handleLancar = async (e) => {
    e.preventDefault(); setSaving(true); setErro(''); setSucesso('')
    try {
      await api.post(`/quotas/${condominioId}/lancar`, { ...form, fracaoId: fracao.id })
      setSucesso(`Quota lançada: ${fmtEuro(fracao.valorQuota)}`)
      setTimeout(() => { setModal(false); setSucesso(''); onDone() }, 1200)
    } catch (err) { setErro(err.response?.data?.erro || 'Erro.') }
    finally { setSaving(false) }
  }

  return (
    <>
      <button className="btn btn-sm" onClick={() => { setErro(''); setSucesso(''); setForm({ periodo, data: hoje() }); setModal(true) }}>
        <Send size={12} /> Lançar
      </button>
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Lançar quota — Fração {fracao.designacao}</span>
              <button onClick={() => setModal(false)} style={{ background: 'none', border: 'none', fontSize: 20 }}>×</button>
            </div>
            <div style={{ padding: 12, background: 'var(--bg)', borderRadius: 'var(--radius)', marginBottom: 16, fontSize: 13 }}>
              Valor: <strong>{fmtEuro(fracao.valorQuota)}</strong>
            </div>
            {erro && <div className="alert alert-error">{erro}</div>}
            {sucesso && <div className="alert alert-success">{sucesso}</div>}
            <form onSubmit={handleLancar}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label className="form-label">Período *</label>
                  <input className="form-input" type="month" value={form.periodo} onChange={e => setForm({ ...form, periodo: e.target.value })} required />
                </div>
                <div className="form-group"><label className="form-label">Data *</label>
                  <input className="form-input" type="date" value={form.data} onChange={e => setForm({ ...form, data: e.target.value })} required />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? <span className="spinner" /> : 'Lançar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
